import React, { useMemo, useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import ProgressBar from "react-bootstrap/ProgressBar";
import Spinner from "react-bootstrap/Spinner";
import useToggle from "react-use/lib/useToggle";

import * as yup from "yup";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory, useParams, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Input from "../../../../components/Input";

import {
  QUERY_CLIENT_SENDGRID,
  QUERY_SENDGRID_OPERATION,
} from "../../../../config/graphql/query";
import {
  UPDATE_CLIENT_SENDGRID,
  DELETE_CLIENT_SENDGRID,
  START_SENDGRID_CLIENT_CREATION,
  START_SENDGRID_TEMPLATE_SYNC,
} from "../../../../config/graphql/mutation";
import useHasRole from "../../../../lib/hooks/useHasRole";

import { useClientId, useClientType } from "../hooks/useClient";
import ColorPicker from "../../../../components/ColorPicker";

import {
  UploadInput,
  UploadPreview,
  useFiles,
  useUploadProvider,
} from "../../../../components/FileUpload";

interface FieldValues {
  synchronize: boolean;
  senderEmail: string;
  senderName?: string;
  color?: string;
  colorToReplace?: string;
  logoURLToReplace?: string;
}

// Creating an account and synchronizing take about a minute, longer than a
// proxied request may stay open: the server runs them in the background and the
// page polls the operation.
type Busy = null | "create" | "save" | "sync";

const POLL_MS = 3000;

const graphqlError = (error: any) =>
  error?.networkError?.result?.errors?.[0]?.message ?? error?.message;

/** Seconds since `startedAt` — from the server, so a reopened page keeps counting. */
const useElapsedSeconds = (startedAt?: string | null) => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!startedAt) {
      setSeconds(0);
      return undefined;
    }

    const started = new Date(startedAt).getTime();
    const tick = () =>
      setSeconds(Math.max(0, Math.floor((Date.now() - started) / 1000)));

    tick();
    const timer = window.setInterval(tick, 1000);

    return () => window.clearInterval(timer);
  }, [startedAt]);

  return seconds;
};

const LogoField = ({ placeholder }: { placeholder?: string }) => {
  const { t } = useTranslation(["client"]);

  return (
    <div className="form-group mb-0">
      <label htmlFor="logo">
        {t("client:client.sendgrid.form.syncSettings.child.logo")}
      </label>
      <UploadInput
        name="logo"
        options={{
          accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
          },
          multiple: false,
        }}
      />
      <div className="mt-2" style={{ maxWidth: "16rem" }}>
        <UploadPreview name="logo" placeholder={placeholder} />
      </div>
    </div>
  );
};

const ColorField = () => {
  const { t } = useTranslation(["client"]);

  return (
    <Controller
      name="color"
      render={({ field: { value, onChange } }) => (
        <div className="form-group">
          <ColorPicker
            label={t("client:client.sendgrid.form.syncSettings.child.color")}
            value={value || undefined}
            onChange={onChange}
          />
        </div>
      )}
    />
  );
};

/** In the templates card: what runs, for how long, and that it takes a while. */
const ProgressPanel = ({
  kind,
  seconds,
}: {
  kind: "create" | "sync";
  seconds: number;
}) => {
  const { t } = useTranslation(["client"]);

  return (
    <div className="w-100 mb-3">
      <div className="d-flex justify-content-between mb-2">
        <strong className="small">
          {t(`client:client.sendgrid.progress.${kind}.title`)}
        </strong>
        <small className="text-muted">
          {t("client:client.sendgrid.progress.elapsed", { seconds })}
        </small>
      </div>
      <ProgressBar animated striped now={100} style={{ height: 6 }} />
      <small className="d-block text-muted mt-2">
        {t(`client:client.sendgrid.progress.${kind}.text`)}
      </small>
    </div>
  );
};

const SendGridClient = React.memo(() => {
  const [show, setShow] = useToggle(false);
  const [showSync, setShowSync] = useToggle(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [operation, setOperation] = useState<Pick<
    ISendGridOperation,
    "id" | "kind" | "startedAt"
  > | null>(null);

  const elapsed = useElapsedSeconds(operation?.startedAt);

  const isAdmin = useHasRole(["ADMIN"]);

  const [files, { onUpload: onUploadLogo }] = useFiles("logo");
  const { onChange: onChangeUploads } = useUploadProvider();

  const pendingLogo = Array.isArray(files) && files.length > 0;

  const { t } = useTranslation(["client", "common"]);
  const history = useHistory();
  const location = useLocation();

  const { id } = useParams<{ id: string }>();

  const clientId = useClientId();

  const clientType = useClientType();

  const isResellerOrDistributor =
    clientType === "reseller" || clientType === "distributor";

  const schema = useMemo(
    () =>
      yup.object().shape({
        senderEmail: yup.string().email(),
        senderName: yup.string().nullable(),
        colorToReplace: yup
          .string()
          .nullable()
          .matches(/^#([A-Fa-f0-9]{6})$/, {
            message: t("client:client.sendgrid.yup.colorFormat"),
            excludeEmptyString: true,
          }),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: {
      synchronize: true,
    },
  });

  const synchronize = methods.watch("synchronize");

  const context = { headers: { ClientId: clientId } };

  const { data: sendGridClientData, refetch } = useQuery<{
    sendGridClient: IClientSendGrid;
  }>(QUERY_CLIENT_SENDGRID, {
    skip: !id,
    variables: { id },
    onCompleted: ({ sendGridClient }) => methods.reset(sendGridClient),
    context,
  });

  const data = sendGridClientData?.sendGridClient;

  const canSynchronize = !!id && !!data && !data.templateSyncBlocker;

  const [onUpdate] = useMutation(UPDATE_CLIENT_SENDGRID, {
    refetchQueries: [
      { query: QUERY_CLIENT_SENDGRID, variables: { id }, context },
    ],
    context,
  });

  const [onStartCreation] = useMutation(START_SENDGRID_CLIENT_CREATION, {
    context,
  });
  const [onStartSync] = useMutation(START_SENDGRID_TEMPLATE_SYNC, { context });
  const [onDelete] = useMutation(DELETE_CLIENT_SENDGRID, { context });

  const { data: operationData } = useQuery<{
    sendGridOperation: ISendGridOperation | null;
  }>(QUERY_SENDGRID_OPERATION, {
    skip: !operation,
    variables: { id: operation?.id },
    pollInterval: POLL_MS,
    fetchPolicy: "network-only",
    context,
  });

  const follow = (started: ISendGridOperation) => {
    setOperation({
      id: started.id,
      kind: started.kind,
      startedAt: started.startedAt,
    });
    setBusy(started.kind);
  };

  // A sync started earlier (another tab, a reload) is followed, not restarted.
  useEffect(() => {
    const running = data?.runningOperation;

    if (running && !operation) {
      setOperation(running);
      setBusy("sync");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.runningOperation?.id]);

  useEffect(() => {
    const current = operationData?.sendGridOperation;

    if (!operation || !current || current.id !== operation.id) {
      return;
    }

    if (current.status === "running") {
      return;
    }

    setOperation(null);
    setBusy(null);

    if (current.status === "failed") {
      toast.error<string>(
        current.message || t("client:client.sendgrid.progress.failed"),
      );
      return;
    }

    if (current.kind === "create") {
      toast.success<string>(t("client:client.information.toast.created"));
      history.replace(
        `/clients/${clientId}/sendgrid/${current.sendgridClient}`,
      );
      return;
    }

    toast.success<string>(
      t("client:client.sendgrid.sync.done", current.result ?? undefined),
    );
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationData]);

  const onBeforeDelete = useCallback(() => {
    history.replace(`/sendgrid`);

    return onDelete({ variables: { id } })
      .then(() => {
        toast.success<string>(t("client:client.information.toast.deleted"));
      })
      .catch((error) => toast.error<string>(graphqlError(error)));
  }, [history, onDelete, id, t]);

  /** Builds the mutation input, uploading a newly picked logo first. */
  const buildInput = async (values: FieldValues) => {
    const { synchronize: sync, ...rest } = values;

    let input: Record<string, unknown> = {
      ...(!id ? { synchronize: sync, client: clientId } : { id }),
      ...rest,
    };

    if (pendingLogo) {
      const uploadedFiles = await onUploadLogo();

      if (uploadedFiles?.[0]) {
        input = { ...input, logo: uploadedFiles[0].file?.id };
      }
    }

    return input;
  };

  /** Saves an existing SendGrid client; true when it worked. */
  const save = async (values: FieldValues, { quiet = false } = {}) => {
    try {
      const {
        data: { updateSendGridClient },
      } = await onUpdate({ variables: { input: await buildInput(values) } });

      methods.reset(updateSendGridClient);
      onChangeUploads({ logo: [] });

      if (!quiet) {
        toast.success<string>(t("client:client.information.toast.updated"));
      }

      return true;
    } catch (error) {
      toast.error<string>(graphqlError(error));
      return false;
    }
  };

  const onSubmit = async (values: FieldValues) => {
    if (!clientId) {
      toast.error<string>(t("client:client.sendgrid.error.missingclient"));
      return;
    }

    if (id) {
      setBusy("save");
      await save(values);
      setBusy(null);
      return;
    }

    setBusy("create");

    try {
      const {
        data: { startSendGridClientCreation },
      } = await onStartCreation({
        variables: { input: await buildInput(values) },
      });

      follow(startSendGridClientCreation);
    } catch (error) {
      setBusy(null);
      toast.error<string>(graphqlError(error));
    }
  };

  // Unsaved colour or logo are saved first — the sync uses what is saved.
  const onSynchronizeTemplates = methods.handleSubmit(async (values) => {
    setShowSync(false);
    setBusy("sync");

    try {
      if (methods.formState.isDirty || pendingLogo) {
        if (!(await save(values, { quiet: true }))) {
          setBusy(null);
          return;
        }
      }

      const {
        data: { startSendGridTemplateSync },
      } = await onStartSync({ variables: { id } });

      follow(startSendGridTemplateSync);
    } catch (error) {
      setBusy(null);
      toast.error<string>(graphqlError(error));
    }
  });

  const working = busy !== null;

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            <Link to="/clients">{t("client:client.token.nav.clients")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`/clients/${clientId}`}>
              {data?.client?.title ?? "Client"}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`/clients/${clientId}/sendgrid`}>
              {t("client:client.sendgrid.nav.sendgrid")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {data?.title ?? "Title"}
          </li>
        </ol>
      </nav>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <fieldset disabled={working}>
            <div className="row">
              <div className="col-lg-4">
                {!!id && (
                  <>
                    <div className="form-group">
                      <label htmlFor="title">
                        {t("client:client.sendgrid.form.title")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={data?.title || ""}
                        disabled
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="senderEmail">
                        {t("client:client.sendgrid.form.senderemail")}
                      </label>
                      <Input name="senderEmail" className="form-control" />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label htmlFor="senderName">
                    {t("client:client.sendgrid.form.sendername")}
                  </label>
                  <Input name="senderName" className="form-control" />
                </div>

                {id && isResellerOrDistributor && (
                  <div>
                    <p className="mb-3 text-primary">
                      {t(
                        "client:client.sendgrid.form.syncSettings.parent.title",
                      )}
                    </p>
                    <div className="form-group">
                      <label htmlFor="apiKeyForCopying">
                        {t(
                          "client:client.sendgrid.form.syncSettings.parent.apiKey",
                        )}
                      </label>
                      <Input name="apiKeyForCopying" className="form-control" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="colorToReplace">
                        {t(
                          "client:client.sendgrid.form.syncSettings.parent.color",
                        )}
                      </label>
                      <Input name="colorToReplace" className="form-control" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="logoURLToReplace">
                        {t(
                          "client:client.sendgrid.form.syncSettings.parent.logoURL",
                        )}
                      </label>
                      <Input name="logoURLToReplace" className="form-control" />
                    </div>
                  </div>
                )}
              </div>

              <div className="col-lg-5 offset-lg-1 mb-4">
                {!id && (
                  <Card>
                    <Card.Header className="bg-white font-weight-bold">
                      {t("client:client.sendgrid.sync.title")}
                    </Card.Header>
                    <Card.Body>
                      <Controller
                        name="synchronize"
                        render={({ field: { value, onChange } }) => (
                          <div className="form-group form-check">
                            <input
                              type="checkbox"
                              id="synchronize"
                              name="synchronize"
                              className="form-check-input"
                              checked={value}
                              onChange={() => onChange(!value)}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="synchronize"
                            >
                              {t(
                                "client:client.sendgrid.form.syncSettings.child.sync",
                              )}
                            </label>
                            <small className="form-text text-muted">
                              {t(
                                "client:client.sendgrid.form.syncSettings.child.title",
                              )}
                            </small>
                          </div>
                        )}
                      />
                      {synchronize && (
                        <>
                          <ColorField />
                          <LogoField />
                        </>
                      )}
                    </Card.Body>
                    {busy === "create" && (
                      <Card.Footer className="bg-white">
                        <ProgressPanel kind="create" seconds={elapsed} />
                      </Card.Footer>
                    )}
                  </Card>
                )}

                {/* Server-enforced: no sync for sources of other clients' templates. */}
                {id && data?.templateSyncBlocker && (
                  <Card>
                    <Card.Header className="bg-white font-weight-bold">
                      {t("client:client.sendgrid.sync.title")}
                    </Card.Header>
                    <Card.Body className="small text-muted">
                      {t(
                        `client:client.sendgrid.sync.blocked.${data.templateSyncBlocker}`,
                      )}
                    </Card.Body>
                  </Card>
                )}

                {canSynchronize && (
                  <Card>
                    <Card.Header className="bg-white font-weight-bold">
                      {t("client:client.sendgrid.sync.title")}
                    </Card.Header>
                    <Card.Body>
                      <p className="small text-muted">
                        {t("client:client.sendgrid.sync.help")}
                      </p>
                      <ColorField />
                      <LogoField placeholder={data?.logoFile?.absolutePath} />
                    </Card.Body>
                    {isAdmin && (
                      <Card.Footer className="bg-white d-flex flex-column align-items-end">
                        {busy === "sync" && (
                          <ProgressPanel kind="sync" seconds={elapsed} />
                        )}
                        <Button
                          variant="outline-primary"
                          onClick={() => setShowSync(true)}
                        >
                          {busy === "sync" ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="mr-2"
                              />
                              {t("client:client.sendgrid.sync.running")}
                            </>
                          ) : (
                            t("client:client.sendgrid.sync.button")
                          )}
                        </Button>
                      </Card.Footer>
                    )}
                  </Card>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center">
              <Button type="submit" variant="primary" className="mr-3">
                {busy === "create" || busy === "save" ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    {t(
                      `client:client.sendgrid.button.${
                        busy === "create" ? "creating" : "updating"
                      }`,
                    )}
                  </>
                ) : (
                  t(
                    `client:client.sendgrid.button.${
                      !!id ? "update" : "create"
                    }`,
                  )
                )}
              </Button>
              {!!id && (
                <>
                  <Link to={`${location.pathname}/templates`}>
                    <Button variant="primary" className="mr-3">
                      {t("client:client.sendgrid.button.viewTemplates")}
                    </Button>
                  </Link>

                  <button
                    onClick={(e) => {
                      e.preventDefault();

                      setShow(true);
                    }}
                    className="btn btn-danger"
                  >
                    {t("common:delete")}
                  </button>
                </>
              )}
            </div>
          </fieldset>
        </form>
      </FormProvider>
      <Modal
        size="sm"
        show={show}
        onHide={setShow}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.information.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.information.modal.title", {
            title: data?.client?.title,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showSync} onHide={setShowSync} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.sendgrid.sync.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.sendgrid.sync.modal.body")}
          <p className="text-danger font-weight-bold mt-2 mb-0">
            {t("client:client.sendgrid.sync.modal.warning")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowSync(false)}
          >
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="primary" onClick={onSynchronizeTemplates}>
            {t("client:client.sendgrid.sync.modal.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default SendGridClient;
